import {Exclude, Expose, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {GeoCoord} from "./geo-coord";
import {Entity} from "./entity";

/** GeofenceEvent type validator */
export const GeofenceEventValidator = t.intersection([
  t.type({
    id: t.string,
    siteId: t.string,
    regionId: t.string,
    personId: t.string,
    inDttm: t.number,
  }),
  t.partial({
    durationInMillis: t.number,
    siteCompanyId: t.string,
    siteCompanyName: t.string,
    parentSiteId: t.string,
    geofenceSiteGroupId: t.string,
    geofenceSiteGroupName: t.string,
    createDttm: t.number,
    personName: t.string,
    vehicleId: t.string,
    vehicleName: t.string,
    trailerId: t.string,
    trailerName: t.string,
    outDttm: t.number,
    latCoord: t.number,
    longCoord: t.number,
    shiftId: t.string,
    siteName: t.string,
  }),
]);

/** Validator for an array of GeofenceEvent types */
export const GeofenceEventArrayValidator = t.array(GeofenceEventValidator);

export type GeofenceEvent = t.TypeOf<typeof GeofenceEventValidator>;
export type GeofenceEventArray = t.TypeOf<typeof GeofenceEventArrayValidator>;

/**
 * A geofence event. Indicates when a person entered and exited a site.
 */
@Exclude()
export class GeofenceEventEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  siteId!: string;

  @Expose()
  regionId!: string;

  @Expose()
  personId!: string;

  @Expose()
  personName?: string;

  @Expose()
  vehicleId?: string;

  @Expose()
  vehicleName?: string;

  @Expose()
  trailerId?: string;

  @Expose()
  trailerName?: string;

  @Expose()
  inDttm!: number;

  @Expose()
  outDttm?: number;

  @Expose()
  durationInMillis?: number

  @Expose()
  siteCompanyId?: string

  @Expose()
  siteCompanyName?: string

  /** Set when the event was recorded against a child of the site it is shown under. */
  @Expose()
  parentSiteId?: string

  @Expose()
  geofenceSiteGroupId?: string

  @Expose()
  geofenceSiteGroupName?: string

  @Expose()
  createDttm?: number;

  @Expose()
  readonly latCoord?: number;

  @Expose()
  readonly longCoord?: number;

  @Expose()
  readonly shiftId?: string;

  @Expose()
  readonly siteName?: string;

  readonly entityType = 'GEOFENCEEVENT';

  get displayName(): Readonly<string> {
    return 'Geofence Event';
  }

  /**
   * Constructor for GeofenceEventEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a GeofenceEventEntity from a GeofenceEvent type value.
   *
   * @param geofenceEvent The geofence event type value.
   */
  static from(geofenceEvent: GeofenceEvent): GeofenceEventEntity {
    return plainToInstance(
      GeofenceEventEntity,
      geofenceEvent,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  get coordinates(): GeoCoord | undefined {
    /* istanbul ignore next */
    if (this.latCoord && this.longCoord) {
      return {
        latCoord: this.latCoord,
        longCoord: this.longCoord,
      };
    } else {
      return undefined;
    }
  }
}
